import React, { useMemo, useState } from 'react';
import { Box, Paper, Table, TableBody, TableCell, TableContainer, TableRow, Typography, TablePagination, Button } from '@mui/material';
import TableHeader from '../layout/table/TableHeader';
import Loader from '../layout/loading/loader';
import {helper} from "../../../utils/helper.js";
import { useNavigate } from 'react-router-dom';
import Constants from '../../../utils/constants';
import { TravelExplore } from '@mui/icons-material';

function getOptionId(opt) {
  if (!opt) return null;
  // support either {id, libelle} or {value, label}
  return (typeof opt.id !== 'undefined') ? opt.id : (typeof opt.value !== 'undefined' ? opt.value : null);
}
function getOptionLabel(opt) {
  if (!opt) return '';
  return opt.libelle || opt.label || opt.nom || String(opt.id ?? opt.value ?? '');
}

function normalizeIdArray(values) {
  if (!Array.isArray(values)) return [];
  return values
    .map(v => getOptionId(v))
    .filter(v => Number.isFinite(Number(v)))
    .map(v => Number(v));
}


const headCells = [
  { id: 'date', label: "Date d'événement", className: 'tableCell-md' },
  { id: 'quartier', label: 'Quartier', className: 'tableCell-md' },
  { id: 'rue', label: 'Rue', className: 'tableCell-md' },
  { id: 'type_pollution', label: 'Type pollution', className: 'tableCell-md' },
  { id: 'surface_m2', label: 'Surface (m²)', className: 'tableCell-md' },
];

function stableSort(array, comparator) {
  const stabilized = array.map((el, index) => [el, index]);
  stabilized.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function getSortableFieldValue(item, key) {
  switch (key) {
    case 'date':
      return item?.dateEvenement || item?.date_evenement || item?.date || item?.createdAt || '';
    case 'quartier':
      return item?.quartier?.nom || item?.quartier || '';
    case 'rue':
      return item?.rue?.nom || item?.rue || '';
    case 'type_pollution':
      return item?.typePollution || item?.type_pollution || '';
    case 'surface_m2':
      return typeof item?.surfaceM2 !== 'undefined' ? item.surfaceM2 : (item?.surface_m2 ?? item?.surface ?? 0);
    default:
      return '';
  }
}

function descendingComparator(a, b, orderBy) {
  if (orderBy === 'date') {
    const da = new Date(getSortableFieldValue(a, 'date')).getTime();
    const db = new Date(getSortableFieldValue(b, 'date')).getTime();
    if (db < da) return -1;
    if (db > da) return 1;
    return 0;
  }
  const va = getSortableFieldValue(a, orderBy);
  const vb = getSortableFieldValue(b, orderBy);
  if (vb < va) return -1;
  if (vb > va) return 1;
  return 0;
}

function GraffitiPatrimoineInterventionsList({ rows = [], loading, error }) {
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('date');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const navigate = useNavigate();

  const sortedRows = useMemo(() => stableSort(rows, getComparator(order, orderBy)), [rows, order, orderBy]);
  const paginatedRows = useMemo(() => {
    const start = page * rowsPerPage;
    const end = start + rowsPerPage;
    return sortedRows.slice(start, end);
  }, [sortedRows, page, rowsPerPage]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = Number(event.target.value);
    setRowsPerPage(value);
    setPage(0);
  };

  // Reset la page quand le tri change ou quand le dataset change
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => { setPage(0); }, [order, orderBy, rows]);

  return (
    <Box>
      {loading && <Loader />}
      {error && (
        <Typography color="error" variant="body2" className="mb-2">
          Erreur lors du chargement des interventions.
        </Typography>
      )}

      <TableContainer component={Paper}>
        <Table
          size="small"

        >
          <TableHeader headCells={headCells} order={order} orderBy={orderBy} onRequestSort={handleRequestSort} noAction={false} />
          <TableBody>
            {paginatedRows.map((r) => {
              const rowId = r.id ?? r.interventionId ?? r.graffitiInterventionId;
              const to = rowId ? Constants.APP_ROUTE_GRAFFITI_INTERVENTION_DETAIL.replace(':id', rowId) : null;
              return (
                <TableRow
                  key={rowId ?? `${getSortableFieldValue(r,'rue')}-${getSortableFieldValue(r,'date')}`}
                  hover
                  onClick={() => { if (to) navigate(to); }}
                  style={{ cursor: to ? 'pointer' : 'default' }}
                >
                  <TableCell className="tableCell-md">{helper.getFormattedDatetime(r.dateEvenement || r.date || r.createdAt)}</TableCell>
                  <TableCell className="tableCell-md">{r.quartier?.nom || r.quartier}</TableCell>
                  <TableCell className="tableCell-md">{r.rue?.nom || r.rue}</TableCell>
                  <TableCell className="tableCell-md">{r.typePollution || r.type_pollution}</TableCell>
                  <TableCell className="tableCell-md" align="right">{(typeof r.surfaceM2 !== 'undefined' ? r.surfaceM2 : (r.surface_m2 ?? r.surface ?? '')) + ' m²'}</TableCell>
                  <TableCell align="center" className="tableCell-md">
                    <Button
                      color="primary"
                      size="small"
                      startIcon={<TravelExplore/>}
                      onClick={(e) => { e.stopPropagation(); if (to) navigate(to); }}
                      disabled={!to}
                    >
                      Détail
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
            {sortedRows.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={headCells.length + 1} align="center">
                  <Typography variant="body2">Aucune intervention trouvée pour les filtres sélectionnés.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <TablePagination
          component="div"
          count={sortedRows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Lignes par page"
        />
      </Box>
    </Box>
  );
}

export default GraffitiPatrimoineInterventionsList;
